import { Icon } from '@iconify-icon/react';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { BreadcrumbJsonLd, FAQPageJsonLd, NextSeo } from 'next-seo';

import Footer from '@/components/layouts/Footer';
import Header from '@/components/layouts/Header';
import type { SearchLandingPageConfig } from '@/content/searchLandingPages';
import PageLink from '@/utils/PageLink';

import CampaignPrivacyProof from './CampaignPrivacyProof';

type SearchLandingPageProps = {
  config: SearchLandingPageConfig;
};

const flowSteps = [
  {
    number: '01',
    title: 'Connect approved knowledge',
    body: 'Upload documents, crawl websites, add text or Q&A, and connect supported knowledge sources.',
  },
  {
    number: '02',
    title: 'Configure the agent',
    body: 'Choose instructions, audience, access rules, appearance, model, and the behavior you want to test.',
  },
  {
    number: '03',
    title: 'Deploy and improve',
    body: 'Publish the experience, review real conversations, and strengthen the sources behind weak answers.',
  },
];

const SearchLandingPage: React.FC<SearchLandingPageProps> = ({ config }) => {
  const canonical = `https://chat.co${config.path}`;
  const isIndustryPage = config.path.startsWith('/solutions/');
  const visibleFaqs = isIndustryPage
    ? config.faqs.filter((faq) => !/soc\s*2/i.test(faq.question))
    : config.faqs;
  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: config.seoTitle,
    description: config.seoDescription,
    url: canonical,
    dateModified: '2026-08-26',
    isPartOf: { '@type': 'WebSite', name: 'Chat.co', url: 'https://chat.co' },
    about: {
      '@type': 'SoftwareApplication',
      name: 'Chat.co',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
    },
  };

  return (
    <div className="bg-white text-gray-900">
      <NextSeo
        title={config.seoTitle.replace(/ \| Chat\.co$/, '')}
        description={config.seoDescription}
        canonical={canonical}
        openGraph={{
          type: 'website',
          url: canonical,
          title: config.seoTitle,
          description: config.seoDescription,
          images: [
            {
              url: 'https://chat.co/images/og-image.png',
              width: 1200,
              height: 630,
              alt: config.breadcrumb,
            },
          ],
        }}
      />
      <BreadcrumbJsonLd
        itemListElements={[
          { position: 1, name: 'Home', item: 'https://chat.co' },
          { position: 2, name: config.breadcrumb, item: canonical },
        ]}
      />
      <FAQPageJsonLd
        mainEntity={visibleFaqs.map((faq) => ({
          questionName: faq.question,
          acceptedAnswerText: faq.answer,
        }))}
      />
      <Head>
        <script
          id={`landing-schema-${config.path.replaceAll('/', '-')}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
        />
      </Head>

      <Header />
      <main>
        <section className="relative overflow-hidden border-b border-gray-100 bg-gradient-to-br from-blue-50 via-white to-cyan-50/50">
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,#0047900a_1px,transparent_1px),linear-gradient(to_bottom,#0047900a_1px,transparent_1px)] bg-[size:3.5rem_3.5rem]" />
          <div className="relative mx-auto grid max-w-7xl gap-12 px-6 py-16 lg:grid-cols-[1.08fr_0.92fr] lg:items-center lg:py-24">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary-600">
                {config.eyebrow}
              </p>
              <h1 className="mt-5 max-w-4xl text-4xl font-extrabold leading-[1.08] tracking-tight text-gray-950 sm:text-6xl">
                {config.title}
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-gray-600 sm:text-xl">
                {config.description}
              </p>
              <ul className="mt-7 space-y-3">
                {config.heroBullets.map((bullet) => (
                  <li
                    key={bullet}
                    className="flex items-start gap-3 text-sm font-medium text-gray-700">
                    <Icon
                      icon="tabler:circle-check-filled"
                      className="mt-0.5 shrink-0 text-lg text-primary-600"
                    />
                    {bullet}
                  </li>
                ))}
              </ul>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Link
                  href={PageLink.login}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-primary-600 px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-500/20 transition hover:-translate-y-0.5 hover:bg-primary-700">
                  Start 14-Day Trial
                  <Icon icon="tabler:arrow-right" className="text-lg" />
                </Link>
                <Link
                  href={PageLink.demo}
                  className="inline-flex items-center justify-center rounded-full border border-gray-300 bg-white px-7 py-3.5 text-sm font-semibold text-gray-800 transition hover:border-primary-300 hover:text-primary-700">
                  Book a demo
                </Link>
              </div>
              <p className="mt-4 text-sm text-gray-500">
                14-day free trial · no credit card required · paid plan required after trial
              </p>
            </div>

            <div className="relative rounded-[30px] border border-blue-100 bg-[#04182e] p-5 shadow-2xl shadow-blue-900/15 sm:p-7">
              <div className="absolute -right-10 -top-10 h-36 w-36 rounded-full bg-primary-500/30 blur-3xl" />
              <div className="relative flex items-center justify-between border-b border-white/10 pb-4">
                <div className="flex items-center gap-2.5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-500 text-white">
                    <Icon icon="tabler:sparkles" className="text-lg" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-white">Chat.co knowledge agent</p>
                    <p className="text-xs text-blue-200/50">Grounded in approved sources</p>
                  </div>
                </div>
                <span className="rounded-full bg-emerald-400/10 px-3 py-1 text-xs font-medium text-emerald-300">
                  Ready
                </span>
              </div>
              <div className="relative mt-6 space-y-4">
                <div className="ml-auto max-w-[88%] rounded-2xl rounded-br-sm bg-primary-600 px-4 py-3 text-sm leading-6 text-white">
                  {config.sampleQuestion}
                </div>
                <div className="max-w-[92%] rounded-2xl rounded-bl-sm bg-white px-4 py-4 text-sm leading-6 text-gray-700">
                  {config.sampleAnswer}
                  <div className="mt-4 flex flex-wrap gap-2 border-t border-gray-100 pt-3">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-primary-700">
                      <Icon icon="tabler:file-text" /> Source 1
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-primary-700">
                      <Icon icon="tabler:external-link" /> Verify answer
                    </span>
                  </div>
                </div>
              </div>
              <div className="relative mt-6 grid grid-cols-3 gap-2 border-t border-white/10 pt-5 text-center text-xs text-blue-200/60">
                <span>Retrieve</span>
                <span>Answer</span>
                <span>Cite</span>
              </div>
            </div>
          </div>
        </section>

        <section
          aria-label="Offer and compliance status"
          className="border-b border-gray-100 bg-white">
          <div className="mx-auto grid max-w-7xl gap-5 px-6 py-7 md:grid-cols-3">
            <div className="flex items-start gap-3">
              <Icon icon="tabler:calendar-check" className="mt-0.5 text-xl text-primary-600" />
              <div>
                <p className="text-sm font-semibold">14-day free trial</p>
                <p className="mt-1 text-xs leading-5 text-gray-500">
                  No permanent free plan; choose a paid plan after the trial.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Icon icon="tabler:quote" className="mt-0.5 text-xl text-primary-600" />
              <div>
                <p className="text-sm font-semibold">Cited, inspectable answers</p>
                <p className="mt-1 text-xs leading-5 text-gray-500">
                  References help users verify the answer against approved sources.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Icon icon="tabler:user-shield" className="mt-0.5 text-xl text-primary-600" />
              <div>
                <p className="text-sm font-semibold">Private access controls</p>
                <p className="mt-1 text-xs leading-5 text-gray-500">
                  Authenticated agents and role-based administration protect private knowledge.
                </p>
              </div>
            </div>
          </div>
        </section>

        {config.proof ? (
          <section
            className="bg-[#f6f8fb] py-14 sm:py-16"
            aria-label={`${config.proof.company} customer proof`}>
            <div className="mx-auto grid max-w-6xl gap-8 px-6 lg:grid-cols-[0.35fr_0.65fr] lg:items-center">
              <div>
                <Image
                  src={config.proof.logo}
                  alt={config.proof.company}
                  width={config.proof.logoWidth}
                  height={config.proof.logoHeight}
                  className="h-12 w-auto object-contain object-left"
                />
                <p className="mt-3 text-xs font-semibold uppercase tracking-[0.16em] text-gray-500">
                  {config.proof.descriptor}
                </p>
              </div>
              <figure className="rounded-[28px] bg-white p-7 shadow-sm ring-1 ring-gray-100 sm:p-9">
                <Icon icon="tabler:quote" className="text-3xl text-primary-600/30" />
                <blockquote className="mt-3 text-xl font-medium leading-8 text-gray-900">
                  “{config.proof.quote}”
                </blockquote>
                <figcaption className="mt-4 text-sm leading-6 text-gray-500">
                  {config.proof.detail}
                </figcaption>
                {config.proof.href ? (
                  <Link
                    href={config.proof.href}
                    className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 hover:underline">
                    Read the case study <Icon icon="tabler:arrow-right" />
                  </Link>
                ) : null}
              </figure>
            </div>
          </section>
        ) : null}

        <section className="mx-auto max-w-7xl px-6 py-20" aria-labelledby="challenge-heading">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary-600">
              The buying problem
            </p>
            <h2
              id="challenge-heading"
              className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
              {config.challengeTitle}
            </h2>
            <p className="mt-5 text-lg leading-8 text-gray-600">{config.challengeDescription}</p>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {config.challenges.map((challenge) => (
              <article key={challenge.title} className="rounded-2xl border border-gray-200 p-7">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-rose-50 text-rose-600">
                  <Icon icon={challenge.icon} className="text-2xl" />
                </span>
                <h3 className="mt-5 text-xl font-bold">{challenge.title}</h3>
                <p className="mt-3 text-sm leading-6 text-gray-600">{challenge.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="bg-[#04182e] py-20 text-white" aria-labelledby="capability-heading">
          <div className="mx-auto max-w-7xl px-6">
            <div className="max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-blue-300">
                The Chat.co approach
              </p>
              <h2
                id="capability-heading"
                className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
                {config.capabilityTitle}
              </h2>
              <p className="mt-5 text-lg leading-8 text-blue-100/70">
                {config.capabilityDescription}
              </p>
            </div>
            <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
              {config.capabilities.map((capability) => (
                <article
                  key={capability.title}
                  className="rounded-2xl border border-white/10 bg-white/[0.05] p-6">
                  <Icon icon={capability.icon} className="text-2xl text-[#e2c06a]" />
                  <h3 className="mt-4 text-lg font-bold">{capability.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-blue-100/65">{capability.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <CampaignPrivacyProof />

        {config.comparison ? (
          <section className="mx-auto max-w-7xl px-6 py-20" aria-labelledby="comparison-heading">
            <div className="flex flex-col justify-between gap-5 lg:flex-row lg:items-end">
              <div className="max-w-3xl">
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary-600">
                  Direct comparison
                </p>
                <h2
                  id="comparison-heading"
                  className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
                  Chat.co and {config.comparison.competitor}: what to test
                </h2>
                <p className="mt-4 text-sm leading-6 text-gray-500">
                  Reviewed {config.comparison.reviewed}. Products change; confirm current
                  capabilities and pricing before purchasing.
                </p>
              </div>
              <a
                href={config.comparison.officialUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 hover:underline">
                {config.comparison.sourceLabel} <Icon icon="tabler:external-link" />
              </a>
            </div>
            <div className="mt-8 overflow-x-auto rounded-2xl border border-gray-200">
              <table className="w-full min-w-[780px] border-collapse text-left text-sm">
                <caption className="sr-only">
                  Chat.co and {config.comparison.competitor} comparison
                </caption>
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-5 py-4 font-semibold">Evaluation area</th>
                    <th className="px-5 py-4 font-semibold text-primary-800">Chat.co</th>
                    <th className="px-5 py-4 font-semibold">{config.comparison.competitor}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 text-gray-700">
                  {config.comparison.rows.map((row) => (
                    <tr key={row.topic}>
                      <th scope="row" className="px-5 py-5 align-top font-semibold text-gray-900">
                        {row.topic}
                      </th>
                      <td className="px-5 py-5 align-top leading-6">{row.chatCo}</td>
                      <td className="px-5 py-5 align-top leading-6">{row.competitor}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}

        <section className="bg-[#f6f8fb] py-20" aria-labelledby="workflow-heading">
          <div className="mx-auto max-w-7xl px-6">
            <div className="grid gap-12 lg:grid-cols-[0.75fr_1.25fr]">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary-600">
                  From source to answer
                </p>
                <h2
                  id="workflow-heading"
                  className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
                  Launch the first useful workflow in three steps
                </h2>
                <p className="mt-5 leading-7 text-gray-600">
                  Start with a narrow, valuable question set. Prove retrieval and citations before
                  expanding the source set.
                </p>
              </div>
              <ol className="divide-y divide-gray-200 border-y border-gray-200">
                {flowSteps.map((step) => (
                  <li
                    key={step.number}
                    className="grid gap-4 py-7 sm:grid-cols-[80px_0.55fr_1fr] sm:items-start">
                    <span className="text-3xl font-light text-primary-300">{step.number}</span>
                    <h3 className="font-bold text-gray-950">{step.title}</h3>
                    <p className="text-sm leading-6 text-gray-600">{step.body}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-6 py-20" aria-labelledby="applications-heading">
          <div className="grid gap-12 lg:grid-cols-[0.7fr_1.3fr]">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary-600">
                High-value starting points
              </p>
              <h2 id="applications-heading" className="mt-3 text-3xl font-bold tracking-tight">
                Choose the first workflow to prove
              </h2>
              <p className="mt-5 leading-7 text-gray-600">
                Use a representative source set and real questions. Evaluate answer quality,
                citation usefulness, access, administration, and total operating effort.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {config.applications.map((application) => (
                <div
                  key={application}
                  className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white p-5 text-sm font-semibold text-gray-800 shadow-sm">
                  <Icon
                    icon="tabler:arrow-up-right"
                    className="shrink-0 text-lg text-primary-600"
                  />
                  {application}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          className="border-y border-gray-200 bg-gray-50 py-20"
          aria-labelledby="faq-heading">
          <div className="mx-auto max-w-4xl px-6">
            <p className="text-center text-sm font-semibold uppercase tracking-[0.18em] text-primary-600">
              FAQ
            </p>
            <h2
              id="faq-heading"
              className="mt-3 text-center text-3xl font-bold tracking-tight sm:text-4xl">
              Questions to resolve before you buy
            </h2>
            <div className="mt-10 divide-y divide-gray-200 rounded-2xl border border-gray-200 bg-white px-6 sm:px-8">
              {visibleFaqs.map((faq) => (
                <article key={faq.question} className="py-7">
                  <h3 className="text-lg font-bold text-gray-950">{faq.question}</h3>
                  <p className="mt-3 text-sm leading-7 text-gray-600">{faq.answer}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-20">
          <div className="mx-auto max-w-7xl px-6">
            <div className="relative overflow-hidden rounded-[32px] bg-primary-600 px-7 py-12 text-center text-white sm:px-12">
              <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,#ffffff10_1px,transparent_1px),linear-gradient(to_bottom,#ffffff10_1px,transparent_1px)] bg-[size:3rem_3rem]" />
              <div className="relative">
                <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                  Test Chat.co with your real questions and sources
                </h2>
                <p className="mx-auto mt-4 max-w-2xl text-base leading-7 text-blue-100">
                  Start a 14-day free trial with no credit card, or book a working session for a
                  more complex deployment.
                </p>
                <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
                  <Link
                    href={PageLink.login}
                    className="inline-flex items-center justify-center rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-primary-700 hover:bg-blue-50">
                    Start 14-Day Trial
                  </Link>
                  <Link
                    href={PageLink.demo}
                    className="inline-flex items-center justify-center rounded-full border border-white/40 px-7 py-3.5 text-sm font-semibold text-white hover:bg-white/10">
                    Book a demo
                  </Link>
                </div>
                <nav
                  aria-label="Related pages"
                  className="mt-8 flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm text-blue-100">
                  {config.related.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      className="hover:text-white hover:underline">
                      {item.label} →
                    </Link>
                  ))}
                </nav>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default SearchLandingPage;
